"use client";

/**
 * Integration requirements checklist with toggles.
 *
 * Extracted from `compass-intake.jsx:625-633`.
 * A vertical list of integration items with toggle switches and optional citations.
 */

import { CitationChip } from "../data-display/CitationChip";

export interface IntegrationItem {
  label: string;
  enabled: boolean;
  /** Optional citation number. */
  cite?: number;
}

export interface IntegrationsProps {
  /** Integration items to display. */
  items: IntegrationItem[];
  /** Called when a toggle is clicked. */
  onToggle?: (index: number) => void;
  /** Called when a citation chip is clicked. */
  onCiteClick?: (n: number) => void;
}

/**
 * Integration checklist with toggle switches.
 */
export function Integrations({
  items,
  onToggle,
  onCiteClick,
}: IntegrationsProps) {
  return (
    <div
      className={[
        "bg-(--bg-white) border border-(--border-subtle)",
        "rounded-(--radius-container)",
        "divide-y divide-(--border-subtle)",
        "overflow-hidden",
      ].join(" ")}
    >
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-3 px-[14px] py-[12px]">
          <span className="flex-1 min-w-0 text-[13.5px] text-(--fg-primary) font-medium">
            {item.label}
          </span>

          {item.cite != null && (
            <CitationChip n={item.cite} onClick={(n) => onCiteClick?.(n)} />
          )}

          {/* Toggle switch */}
          <button
            type="button"
            className={[
              "flex-shrink-0 relative",
              "w-[30px] h-[17px]",
              "rounded-full border-0 p-0",
              "cursor-pointer",
              "transition-colors duration-300",
              item.enabled ? "bg-(--steel-80)" : "bg-(--bg-hover)",
            ].join(" ")}
            onClick={() => onToggle?.(i)}
            aria-label={`Toggle ${item.label}`}
          >
            <span
              className={[
                "absolute top-[2px] w-[13px] h-[13px]",
                "rounded-full bg-(--bg-white)",
                "shadow-(--shadow-xs)",
                "transition-[left] duration-300",
                item.enabled ? "left-[15px]" : "left-[2px]",
              ].join(" ")}
            />
          </button>
        </div>
      ))}
    </div>
  );
}
